import { useEffect, useState } from "react";
import { useWorkingHours, useReplaceWorkingHours } from "../api/queries";
import { ApiError } from "../api/client";
import { type Weekday, type WorkingHoursEntryInput } from "../api/types";

// Display order: Sunday-first (Israeli work week).
const DISPLAY_ORDER: Weekday[] = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

const DEFAULTS: Record<Weekday, boolean> = {
  Sunday: true,
  Monday: true,
  Tuesday: true,
  Wednesday: true,
  Thursday: true,
  Friday: false,
  Saturday: false,
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function toMinutes(t: string): number {
  const [h, m] = t.split(":");
  return Number(h) * 60 + Number(m);
}

function makeBlank(): WorkingHoursEntryInput[] {
  return DISPLAY_ORDER.map((day) => ({
    day,
    enabled: DEFAULTS[day],
    start_time: "09:00",
    end_time: "17:00",
  }));
}

export default function WorkingHoursEditor() {
  const query = useWorkingHours();
  const save = useReplaceWorkingHours();
  const [rows, setRows] = useState<WorkingHoursEntryInput[]>(makeBlank());
  const [status, setStatus] = useState<null | { kind: "ok" | "err"; text: string }>(null);

  useEffect(() => {
    if (query.data?.entries) {
      const byDay = new Map(query.data.entries.map((e) => [e.day, e]));
      setRows(
        DISPLAY_ORDER.map((day) => {
          const e = byDay.get(day);
          return e
            ? { day, enabled: e.enabled, start_time: e.start_time, end_time: e.end_time }
            : { day, enabled: DEFAULTS[day], start_time: "09:00", end_time: "17:00" };
        }),
      );
    }
  }, [query.data]);

  function update(day: Weekday, patch: Partial<WorkingHoursEntryInput>) {
    setRows((prev) => prev.map((r) => (r.day === day ? { ...r, ...patch } : r)));
    setStatus(null);
  }

  async function onSave() {
    setStatus(null);
    for (const r of rows) {
      if (!HHMM.test(r.start_time) || !HHMM.test(r.end_time)) {
        setStatus({ kind: "err", text: `${r.day}: times must be HH:MM 24h` });
        return;
      }
      if (!r.enabled) continue;
      const s = toMinutes(r.start_time);
      const e = toMinutes(r.end_time);
      if (s === e) {
        setStatus({ kind: "err", text: `${r.day}: start and end cannot be equal` });
        return;
      }
      if (s > e) {
        setStatus({ kind: "err", text: `${r.day}: start time must be before end time` });
        return;
      }
    }
    try {
      await save.mutateAsync(rows);
      setStatus({ kind: "ok", text: "Saved." });
    } catch (err) {
      const detail = err instanceof ApiError ? String(err.detail ?? err.message) : String(err);
      setStatus({ kind: "err", text: detail });
    }
  }

  if (query.isLoading) return <p className="text-sm text-slate-400">Loading working hours…</p>;
  if (query.isError) return <p className="text-sm text-rose-400">Failed to load working hours.</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400">
        Reference only — working hours are not enforced globally. Apply them to a rule via the
        Days/Start/End fields in the rule editor.
      </p>
      <div className="overflow-hidden rounded-xl border border-slate-800">
        <div className="grid grid-cols-[minmax(0,1fr)_5rem_7rem_7rem] items-center gap-3 bg-slate-900/60 px-4 py-2 text-xs uppercase tracking-wide text-slate-400">
          <span>Day</span>
          <span className="text-center">Enabled</span>
          <span className="text-center">Start</span>
          <span className="text-center">End</span>
        </div>
        <ul className="divide-y divide-slate-800">
          {rows.map((row) => (
            <li
              key={row.day}
              className={`grid grid-cols-[minmax(0,1fr)_5rem_7rem_7rem] items-center gap-3 px-4 py-3 text-sm ${
                row.enabled ? "bg-slate-900/30" : "bg-slate-900/10 text-slate-500"
              }`}
            >
              <span className="font-medium">{row.day}</span>
              <div className="flex justify-center">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => update(row.day, { enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-600 bg-slate-800 accent-brand"
                />
              </div>
              <input
                type="time"
                value={row.start_time}
                onChange={(e) => update(row.day, { start_time: e.target.value })}
                disabled={!row.enabled}
                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm outline-none focus:border-brand disabled:opacity-50"
                step={60}
              />
              <input
                type="time"
                value={row.end_time}
                onChange={(e) => update(row.day, { end_time: e.target.value })}
                disabled={!row.enabled}
                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm outline-none focus:border-brand disabled:opacity-50"
                step={60}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between">
        <div className="min-h-[1.25rem] text-sm">
          {status ? (
            <span className={status.kind === "ok" ? "text-emerald-400" : "text-rose-400"}>
              {status.text}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={save.isPending}
          className="btn-primary"
        >
          {save.isPending ? "Saving…" : "Save working hours"}
        </button>
      </div>
    </div>
  );
}
